export const RECONCILIATION_SUMMARY_SCHEMA_VERSION = 1 as const;

export interface ReconciliationSummary {
  schema_version: typeof RECONCILIATION_SUMMARY_SCHEMA_VERSION;
  progress_paid_ex_gst: number;
  variations_paid_ex_gst: number;
  hire_paid_ex_gst: number;
  subtotal_before_retention_ex_gst: number;
  retention_pct: number;
  retention_held_ex_gst: number;
  retention_released_ex_gst: number;
  taxable_subtotal_ex_gst: number;
  gst_amount: number;
  calculated_payment_inc_gst: number;
  amount_received_inc_gst: number;
  difference_inc_gst: number;
}

export interface BuildReconciliationSummaryInput {
  progressPaidExGst: number;
  variationsPaidExGst: number;
  hirePaidExGst: number;
  retentionPct: number;
  retentionHeldEnabled: boolean;
  retentionReleasedExGst: number;
  amountReceivedIncGst: number;
}

export interface ReconciliationSummaryLineItem {
  line_type: "progress" | "variation" | "retention" | "hire";
  action: string;
  paid_amount?: number | null;
}

export interface BuildReconciliationSummaryFromLineItemsInput {
  lineItems: ReconciliationSummaryLineItem[];
  retentionPct: number;
  retentionHeldEnabled: boolean;
  amountReceivedIncGst: number;
}

export interface ClaimsReportFinancialSource {
  claimed_to_date?: number | null;
  variations_this_month?: number | null;
  variations_outstanding?: number | null;
  claim_this_month?: number | null;
  subtotal?: number | null;
  retention_amount?: number | null;
  retention_release_amount?: number | null;
  retention_received_amount?: number | null;
  total_exc_gst?: number | null;
  total_inc_gst?: number | null;
  paid_by_builder?: number | null;
  difference?: number | null;
  reconciliation_summary?: unknown;
}

export interface ClaimsReportDisplayFinancials {
  source: "claim" | "reconciliation";
  claimed: number;
  variations: number;
  thisClaim: number;
  retentionHeld: number;
  retentionRelease: number;
  totalExcGst: number;
  totalIncGst: number;
  amountReceived: number;
  difference: number;
}

export function buildReconciliationSummary(
  input: BuildReconciliationSummaryInput,
): ReconciliationSummary {
  const progressPaidExGst = roundCurrency(input.progressPaidExGst);
  const variationsPaidExGst = roundCurrency(input.variationsPaidExGst);
  const hirePaidExGst = roundCurrency(input.hirePaidExGst);
  const retentionReleasedExGst = roundCurrency(
    Math.max(0, input.retentionReleasedExGst),
  );
  const result = calculatePaymentReconciliation({
    progressPaid: progressPaidExGst,
    variationsPaid: roundCurrency(variationsPaidExGst + hirePaidExGst),
    retentionPct: input.retentionPct,
    retentionHeldEnabled: input.retentionHeldEnabled,
    retentionReleased: retentionReleasedExGst,
    amountReceived: input.amountReceivedIncGst,
  });

  return {
    schema_version: RECONCILIATION_SUMMARY_SCHEMA_VERSION,
    progress_paid_ex_gst: progressPaidExGst,
    variations_paid_ex_gst: variationsPaidExGst,
    hire_paid_ex_gst: hirePaidExGst,
    subtotal_before_retention_ex_gst: result.subtotalExcGst,
    retention_pct: input.retentionHeldEnabled
      ? roundCurrency(Math.max(0, input.retentionPct))
      : 0,
    retention_held_ex_gst: result.retentionHeldAmount,
    retention_released_ex_gst: retentionReleasedExGst,
    taxable_subtotal_ex_gst: roundCurrency(
      result.subtotalIncGst - result.gstAmount,
    ),
    gst_amount: result.gstAmount,
    calculated_payment_inc_gst: result.calculatedPaymentAmount,
    amount_received_inc_gst: result.amountReceived,
    difference_inc_gst: result.difference,
  };
}

export function buildReconciliationSummaryFromLineItems({
  lineItems,
  retentionPct,
  retentionHeldEnabled,
  amountReceivedIncGst,
}: BuildReconciliationSummaryFromLineItemsInput): ReconciliationSummary {
  const receivedForSchedule = (item: ReconciliationSummaryLineItem): number =>
    item.action === "paid" || item.action === "agreement_made"
      ? Number(item.paid_amount || 0)
      : 0;
  const sumType = (lineType: ReconciliationSummaryLineItem["line_type"]): number =>
    lineItems
      .filter((item) => item.line_type === lineType)
      .reduce((sum, item) => sum + receivedForSchedule(item), 0);
  const retentionReleased = lineItems
    .filter((item) => item.line_type === "retention")
    .reduce(
      (sum, item) =>
        sum + (item.action === "paid" ? Number(item.paid_amount || 0) : 0),
      0,
    );

  return buildReconciliationSummary({
    progressPaidExGst: sumType("progress"),
    variationsPaidExGst: sumType("variation"),
    hirePaidExGst: sumType("hire"),
    retentionPct,
    retentionHeldEnabled,
    retentionReleasedExGst: retentionReleased,
    amountReceivedIncGst,
  });
}

const SUMMARY_NUMBER_FIELDS = [
  "progress_paid_ex_gst",
  "variations_paid_ex_gst",
  "hire_paid_ex_gst",
  "subtotal_before_retention_ex_gst",
  "retention_pct",
  "retention_held_ex_gst",
  "retention_released_ex_gst",
  "taxable_subtotal_ex_gst",
  "gst_amount",
  "calculated_payment_inc_gst",
  "amount_received_inc_gst",
  "difference_inc_gst",
] as const;

export function parseReconciliationSummary(value: unknown): ReconciliationSummary | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  if (record.schema_version !== RECONCILIATION_SUMMARY_SCHEMA_VERSION) return null;
  if (
    SUMMARY_NUMBER_FIELDS.some(
      (field) => typeof record[field] !== "number" || !Number.isFinite(record[field]),
    )
  ) {
    return null;
  }

  return {
    schema_version: RECONCILIATION_SUMMARY_SCHEMA_VERSION,
    progress_paid_ex_gst: record.progress_paid_ex_gst as number,
    variations_paid_ex_gst: record.variations_paid_ex_gst as number,
    hire_paid_ex_gst: record.hire_paid_ex_gst as number,
    subtotal_before_retention_ex_gst:
      record.subtotal_before_retention_ex_gst as number,
    retention_pct: record.retention_pct as number,
    retention_held_ex_gst: record.retention_held_ex_gst as number,
    retention_released_ex_gst: record.retention_released_ex_gst as number,
    taxable_subtotal_ex_gst: record.taxable_subtotal_ex_gst as number,
    gst_amount: record.gst_amount as number,
    calculated_payment_inc_gst: record.calculated_payment_inc_gst as number,
    amount_received_inc_gst: record.amount_received_inc_gst as number,
    difference_inc_gst: record.difference_inc_gst as number,
  };
}

export function deriveClaimsReportDisplayFinancials(
  report: ClaimsReportFinancialSource,
): ClaimsReportDisplayFinancials {
  const summary = parseReconciliationSummary(report.reconciliation_summary);
  if (summary) {
    return {
      source: "reconciliation",
      claimed: summary.progress_paid_ex_gst,
      variations: summary.variations_paid_ex_gst,
      thisClaim: summary.subtotal_before_retention_ex_gst,
      retentionHeld: summary.retention_held_ex_gst,
      retentionRelease: summary.retention_released_ex_gst,
      totalExcGst: summary.taxable_subtotal_ex_gst,
      totalIncGst: summary.calculated_payment_inc_gst,
      amountReceived: summary.amount_received_inc_gst,
      difference: summary.difference_inc_gst,
    };
  }

  return {
    source: "claim",
    claimed: Number(report.claimed_to_date || 0),
    variations: roundCurrency(
      Number(report.variations_this_month || 0) +
        Number(report.variations_outstanding || 0),
    ),
    thisClaim: Number(report.claim_this_month ?? report.subtotal ?? 0),
    retentionHeld: Number(report.retention_amount || 0),
    retentionRelease: Math.max(
      Number(report.retention_release_amount || 0),
      Number(report.retention_received_amount || 0),
    ),
    totalExcGst: Number(report.total_exc_gst || 0),
    totalIncGst: Number(report.total_inc_gst || 0),
    amountReceived: Number(report.paid_by_builder || 0),
    difference: Number(report.difference || 0),
  };
}

export function getOperationalRetentionAmount(
  report: Pick<ClaimsReportFinancialSource, "retention_amount" | "reconciliation_summary">,
): number {
  return (
    parseReconciliationSummary(report.reconciliation_summary)?.retention_held_ex_gst ??
    Number(report.retention_amount || 0)
  );
}

export function getOperationalRetentionPct(
  report: Pick<ClaimsReportFinancialSource, "reconciliation_summary"> & {
    retention_pct?: number | null;
  },
): number {
  return (
    parseReconciliationSummary(report.reconciliation_summary)?.retention_pct ??
    Number(report.retention_pct || 0)
  );
}
import {
  calculatePaymentReconciliation,
  roundCurrency,
} from "./claimFinancials";
